import { useEffect, useState } from 'react';
import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import { Status as ClientStatus } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getCurrentAIScribe } from 'store/aiScribe/aiScribeActions';
import { isAiScribeRecordingStatus } from 'store/aiScribe/aiScribeSlice';
import clsx from 'clsx';

import { NoteForm } from '../NoteForm';
import { PopupDraftNoteFormProvider, useNoteFormContext } from '../context';
import { useIsTranscribing } from '../hooks/useIsTranscribing';
import { AiScribeMedia, PopupDraftActions } from './NoteFormSections';
import { MpiNoteActions } from './MpiNoteActions';
import style from '../noteEdit.module.scss';

type PopupNoteFormProps = {
  status: ClientStatus | ClientStatus['id'];
  clientId: string;
  note?: ClientProfileContentItem;
  disabled?: boolean;
};

const RecordingInProgressBanner = () => (
  <div className={style.recordingInProgressBanner}>
    <span className={clsx('material-icons', style.recordingInProgressIcon)}>
      fiber_manual_record
    </span>
    <p>AI Scribe recording in progress.</p>
  </div>
);

const PopupNoteFormContent = ({
  onEdit,
  showMpiActions
}: {
  onEdit?: () => void;
  showMpiActions: boolean;
}) => {
  const { enableDateTimeUpdates } = useFlags();
  const { isManagerReview, disabled, clientId, note } = useNoteFormContext();
  const isAiScribeTranscribing = useIsTranscribing();
  const aiScribeRecording = useAppSelector((state) => state.aiScribe);
  const isRecordingNote =
    note?.draft &&
    note.id != null &&
    aiScribeRecording.clientNoteId != null &&
    Number(note.id) === aiScribeRecording.clientNoteId &&
    isAiScribeRecordingStatus(aiScribeRecording.status);

  return (
    <NoteForm.Form>
      <NoteForm.FieldsContainer expanded={true}>
        <NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.NoteType disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.DateOfEncounter disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
        </NoteForm.FieldsContainer.Row>

        {enableDateTimeUpdates ? (
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.StartTime disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.TimeSpent disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
        ) : (
          !isManagerReview && (
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.StartTime disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.TimeSpent disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
          )
        )}

        {isManagerReview && (
          <>
            {enableDateTimeUpdates ? (
              <>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Manager disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Chw disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.FullWidth>
                    <NoteForm.Fields.Provider disabled={disabled} />
                  </NoteForm.FieldsContainer.FullWidth>
                </NoteForm.FieldsContainer.Row>
              </>
            ) : (
              <>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.TimeSpent disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Manager disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Chw disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Provider disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
              </>
            )}
          </>
        )}

        {!isManagerReview && (
          <>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.NextContact disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.WhoDidYouReach disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.FullWidth>
              <NoteForm.Fields.TypeOfSupport disabled={disabled} />
            </NoteForm.FieldsContainer.FullWidth>
          </>
        )}

        <AiScribeMedia />
        <NoteForm.FieldsContainer.HalfRow>
          {isRecordingNote && <RecordingInProgressBanner />}
          <NoteForm.Fields.Note disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.DocumentationTime disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
        <MpiNoteActions clientId={clientId} visible={showMpiActions} />
      </NoteForm.FieldsContainer>
      <PopupDraftActions
        onEdit={onEdit}
        deleteDisabled={isRecordingNote}
        submitDisabled={isRecordingNote || isAiScribeTranscribing}
      />
    </NoteForm.Form>
  );
};

/**
 * Preset component for editing notes in a popup.
 * Disabled state is controlled from outside via props.
 * Includes AI Scribe status, transcript, and audio player.
 * Autosave is enabled only for draft notes.
 */
export const PopupNoteForm = ({ status, clientId, note, disabled = true }: PopupNoteFormProps) => {
  const { data } = useAppSelector((state) => state.clients.client);
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(!disabled);
  const onEdit = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    dispatch(getCurrentAIScribe());

    const channel = new BroadcastChannel('ai-scribe-update');
    channel.onmessage = (event) => {
      if (event.data?.message === 'AI_SCRIBE_RECORDING_SUBMITTED') {
        dispatch(getCurrentAIScribe());
      }
    };

    return () => {
      channel.close();
    };
  }, [dispatch]);

  return (
    <PopupDraftNoteFormProvider
      status={status}
      clientId={clientId}
      note={note}
      disabled={!isEditing}
      clientName={data?.firstName + ' ' + data?.lastName ?? ''}
      prependedClientId={data?.prependedId ?? ''}
    >
      <NoteForm.Root>
        <PopupNoteFormContent onEdit={onEdit} showMpiActions={isEditing} />
      </NoteForm.Root>
    </PopupDraftNoteFormProvider>
  );
};
